import { createSlice } from "@reduxjs/toolkit";
import { createPaymentIntent, fetchPaymentProvider } from "./paymentThunks";

interface PaymentState {
  paymentMethod: string | null;
  orderStatus: string | null;
  clientSecret?: string | null;
  paymentId?: string | null;
  paymentSession?: Record<string, unknown> | null;
  availablePayments: any[];
  countryCode: string | null;
  currency: string | null;
  error: string | null;
  receiptStatus?: string | null;
  activationProgress?: { step?: string; percent?: number; details?: unknown } | null;
  activeProvider: string | null;
  availableProviders: string[];
  backupOrder: string[];
  providerLoading: boolean;
}

const initialState: PaymentState = {
  paymentMethod: null,
  orderStatus: null,
  clientSecret: null,
  paymentId: null,
  paymentSession: null,
  availablePayments: [],
  countryCode: null,
  currency: null,
  error: null,
  activeProvider: null,
  availableProviders: [],
  backupOrder: [],
  providerLoading: false,
};

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    clearPaymentData: (state) => {
      state.clientSecret = null;
      state.paymentSession = null;
      state.paymentId = null;
      state.orderStatus = null;
      state.error = null;
      state.availablePayments = [];
      state.countryCode = null;
      state.currency = null;
    },
    setClientSecret: (state, action) => {
      state.clientSecret = action.payload;
    },
    setPaymentSession: (state, action) => {
      state.paymentSession = action.payload;
    },
    setPaymentId: (state, action) => {
      state.paymentId = action.payload;
    },
    setPaymentStatus: (state, action) => {
      state.orderStatus = action.payload;
    },
    setReceiptStatus: (state, action) => {
      state.receiptStatus = action.payload?.status ?? String(action.payload ?? "");
    },
    setActivationProgress: (state, action) => {
      state.activationProgress = action.payload ?? null;
    },
    setError: (state, action) => {
      state.error = typeof action.payload === "string" ? action.payload : JSON.stringify(action.payload ?? {});
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createPaymentIntent.fulfilled, (state, action) => {
        state.clientSecret = action.payload.clientSecret;
        state.paymentId = action.payload.paymentId;
        state.paymentSession = action.payload.paymentSession;
        state.availablePayments = action.payload.availablePayments ?? [];
        state.countryCode = action.payload.countryCode ?? null;
        state.currency = action.payload.currency ?? null;
      })
      .addCase(createPaymentIntent.rejected, (state, action) => {
        state.error = action.payload as string;
        state.availablePayments = [];
        state.countryCode = null;
        state.currency = null;
      })
      .addCase(fetchPaymentProvider.pending, (state) => {
        state.providerLoading = true;
      })
      .addCase(fetchPaymentProvider.fulfilled, (state, action) => {
        state.providerLoading = false;
        state.activeProvider = action.payload.activeProvider;
        state.availableProviders = action.payload.availableProviders;
        state.backupOrder = action.payload.backupOrder;
      })
      .addCase(fetchPaymentProvider.rejected, (state, action) => {
        state.providerLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearPaymentData, setClientSecret, setPaymentId, setPaymentSession, setPaymentStatus, setReceiptStatus, setActivationProgress, setError } =
  paymentSlice.actions;

export default paymentSlice.reducer;
